import DomPurify from "isomorphic-dompurify";
import { ArrowRightCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import VideoPlayer from "./AtomParts/Video";
import "./atom.css";
import { AtomProps } from "./types";

const Atom: React.FC<AtomProps> = ({
  bgColor,
  assetType,
  imageUrl,
  linkUrl,
  isTagTop = false,
  videoId,
  iconUrl,
  iconClassName,
  title,
  atmc,
  description,
  tags,
  iconWidth,
  buttonUrl = null,
  buttonText = null,
  buttonVariant = "primary",
  iconHeight,
  mediaHeight = 100, // Added this prop
  className,
  subtitle,
  textColor = "text-primary-700", // New prop with a default value
  titleClassName,
  descClassName,
  imgSizes,
  colSpan,
  isLeftAlign
}) => {
  let colSpanClassName = "col-span-1";

  if (colSpan === 2) {
    colSpanClassName = "col-span-1 sm:col-span-2";
  } else if (colSpan === 3) {
    colSpanClassName = "col-span-1 sm:col-span-3";
  }

  const renderTags = () => (
    <div className={`mt-4 flex items-center gap-x-1.5 ${isTagTop ? "absolute top-0" : "absolute bottom-3 left-8"}`}>
      {tags?.map((tag) => (
        <Badge key={tag.label} className="px-1.5 py-[.5px]">
          {tag.label}
        </Badge>
      ))}
    </div>
  );

  const renderButton = () => {
    if (buttonUrl && buttonText) {
      return (
        <div className="self-end py-2">
          <Button variant={(buttonVariant as any) || "primary"} size="default" className="rounded-full" asChild>
            <Link href={buttonUrl}> {buttonText}</Link>
          </Button>
        </div>
      );
    }
    return null;
  };

  const renderTitle = () => {
    if (title) {
      return (
        <h3
          className={`${titleClassName} font-heading text-primary-700`}
          style={{ color: textColor }}
          dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title) }}
        />
      );
    }
    return null;
  };

  const textAlignmentClass = isLeftAlign ? "text-left" : "text-center";

  const renderDescription = () => {
    let fullDescription = "";
    if (subtitle) {
      fullDescription += `<i>${subtitle}</i> `;
    }
    fullDescription += description || "";
    return (
      <p
        className={`${descClassName} pb-4 font-matter text-md font-normal text-primary-700 `}
        style={{ color: textColor }}
        dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(fullDescription) }}
      />
    );
  };

  const renderAsset = () => {
    switch (assetType) {
      case "image":
        return (
          <div
            style={{ backgroundColor: bgColor }}
            className={`image-styles overflow-hidden rounded-2xl ${className} ${atmc} ${colSpanClassName}`}
          >
            <div className={`content-wrapper ${textAlignmentClass}`}>
              <div className={`relative w-full pb-2`} style={{ height: mediaHeight }}>
                {imageUrl && (
                  <Image
                    alt={`SoldAve renovation review, ${title}`}
                    src={imageUrl}
                    fill
                    sizes={imgSizes}
                    style={{
                      objectFit: "cover"
                    }}
                  />
                )}
              </div>

              <div className={`relative flex-col justify-center px-8 pb-10 pt-12  ${!imageUrl ? "items-center" : ""}`}>
                {iconUrl && iconWidth && iconHeight && (
                  <div className="relative mb-3">
                    {isTagTop && !!tags?.length && renderTags()}
                    {imageUrl && <Image src={iconUrl} width={iconWidth} height={iconHeight} alt={title || ""} />}
                  </div>
                )}
                {isTagTop && !!tags?.length && renderTags()}
                {renderTitle()}
                {renderDescription()}
                {!isTagTop && !!tags?.length && renderTags()}
                {renderButton()}
              </div>
            </div>
          </div>
        );
      case "icon":
        return (
          <div
            style={{ backgroundColor: bgColor }}
            className={`icon-styles flex flex-row ${
              isLeftAlign ? "items-start" : "items-center"
            } lead rounded-2xl px-2 py-8 md:px-8 md:py-8 ${className} ${colSpanClassName}`}
          >
            <div className={`content-wrapper ${textAlignmentClass} ${atmc} `}>
              {iconUrl ? (
                <div
                  className={`relative flex w-full ${
                    isLeftAlign ? "justify-start" : "justify-center"
                  } pb-2 ${iconClassName || ""}`}
                >
                  <Image src={iconUrl} alt="test" width={iconWidth || 50} height={iconHeight || 50} />
                </div>
              ) : null}
              <div className={`flex flex-col ${iconUrl ? "" : "h-full items-center justify-center"}`}>
                {renderTitle()}
                {renderDescription()}
                {renderButton()}
              </div>
            </div>
          </div>
        );
      case "video":
        return (
          <div
            style={{ backgroundColor: bgColor }}
            className={`video-styles overflow-hidden rounded-2xl ${className} ${colSpanClassName}`}
          >
            <div className={`content-wrapper ${textAlignmentClass}`}>
              <div className={`relative w-full pb-2`} style={{ height: mediaHeight }}>
                <VideoPlayer id={videoId || ""} />
              </div>
              <div className="relative px-8 py-10">
                {isTagTop && !!tags?.length && renderTags()}
                {title && (
                  <h3 className={`${titleClassName} font-heading text-primary-700`} style={{ color: textColor }}>
                    {title}
                  </h3>
                )}
                {renderDescription()}
                {!isTagTop && !!tags?.length && renderTags()}
                {renderButton()}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  const renderAssetWithLink = () => {
    const assetContent = renderAsset();

    if (linkUrl) {
      return (
        <Link href={linkUrl} legacyBehavior>
          <a className={`atom-link block transform transition-transform  duration-300 ${className} hoverable-atom`}>
            {assetContent}
            <ArrowRightCircle strokeWidth={2} color="#e5b04f" className="hover-icon" />{" "}
            {/* This is the icon that appears on hover */}
          </a>
        </Link>
      );
    }

    return assetContent;
  };

  return renderAssetWithLink();
};

export default Atom;
